import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material';
import {select, Store} from '@ngrx/store';

import {AuthService, OrgService, UserService} from '../core';
import * as fromRoot from '../reducers';
import * as fromAfbApi from '../afb-api/reducers';
import * as fromAfbGraphqlApi from '../afb-graphql-api/reducers';
import * as fromDashboard from './reducers';
import * as trigger from '../core/store/actions/trigger.action';

import {DashboardService} from './shared';
import {TimelineComponent} from './timeline/timeline.component';
import {WelcomeComponent} from './onboard/welcome.component';

import {Observable} from 'rxjs';

import {ListPaneType} from './enums/list-pane-type.enum';
import {OrganizationWithToken} from '../core/models/core';
import {SelfOrganizationUser} from '../afb-api/models/organization-user';
import {filter, withLatestFrom} from 'rxjs/operators';
import {Device, DeviceUuid} from '../afb-api/models/device';
import {DeviceType} from '../afb-api/enums/device-type.enum';
import {StartAirDataPoll} from '../afb-api/actions/air-data-poll.actions';
import {EmptySelectedDevice} from './actions/dashboard-device.actions';
import {Airnote} from '../afb-api/models/airnote';
import {TriggerEvent} from '../afb-api/models/trigger-event';
import {OrganizationUserRole} from '../afb-api/enums/organization-user-role.enum';
import {SwitchListPane} from './actions/dashboard-view.actions';
import {Subject} from 'rxjs/index';
import {takeUntil} from 'rxjs/internal/operators';

@Component({
  selector: 'awair-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild(TimelineComponent) timelineComp: TimelineComponent;

  // Backdrops
  isDeviceGridBackdrop: boolean;
  isDeviceGridTransparentBackdrop: boolean;
  isTimelineBackdrop: boolean;
  isFeedBackdrop: boolean;
  backdropColor: string = '';
  backdropOpacity: number = 0;
  backdropZIndex: number = 0;
  transparentBackdropZIndex: number = 0;

  OrganizationUserRole = OrganizationUserRole;

  // To use this enum in template
  ListPaneType = ListPaneType;

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );
  selectedListPane$: Observable<ListPaneType> = this.dashboardStore.pipe(
    select(fromDashboard.selectedListPane)
  );
  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter((selfOrganizationUser: SelfOrganizationUser) => selfOrganizationUser != null)
  );

  devices$: Observable<Device[]> = this.afbApiStore.pipe(
    select(fromAfbApi.devices),
    filter((devices: Device[]) => devices != null)
  );

  accessToken$: Observable<string> = this.afbGraphqlApiStore.pipe(
    select(fromAfbGraphqlApi.accessToken),
    filter((accessToken: string) => accessToken != null)
  );

  private destroyed$ = new Subject<boolean>();
  private devices: Device[] = [];

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private afbGraphqlApiStore: Store<fromAfbGraphqlApi.State>,
              private dashboardStore: Store<fromDashboard.State>,
              private authService: AuthService,
              private orgService: OrgService,
              private userService: UserService,
              private dashboardService: DashboardService,
              private cdRef: ChangeDetectorRef,
              private elementRef: ElementRef,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog) {
  }

  ngOnInit() {
    // Retrieve triggers after deviceList has been retrieved
    // Skip the first initial value
    this.devices$.pipe(
      takeUntil(this.destroyed$),
      withLatestFrom(this.selectedOrganization$)
    ).subscribe(([devices, selectedOrganization]: [Device[], OrganizationWithToken]) => {
      this.devices = devices;

      if (devices.length > 0) {
        this.rootStore.dispatch(new trigger.RetrieveTriggerAction({
          orgToken: selectedOrganization.token,
          deviceList: devices
        }));

        const deviceUuids: DeviceUuid[] = devices.map((device: Device) => {
          return {
            deviceType: <DeviceType>device.deviceType,
            deviceId: device.deviceId
          };
        });

        this.afbApiStore.dispatch(new StartAirDataPoll({
          deviceUuids,
          organizationToken: selectedOrganization.token
        }));
      }
    });

    // Device-grid backdrop subscription
    this.dashboardService.deviceGridBackdrop$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (backdropData: { isShow: boolean, backgroundColor?: string, opacity?: number, zIndex?: number }) => {
        if (backdropData.isShow) {
          this.backdropColor = backdropData.backgroundColor;
          this.backdropOpacity = backdropData.opacity;
          this.backdropZIndex = backdropData.zIndex;
        }
        this.isDeviceGridBackdrop = backdropData.isShow;
        this.cdRef.detectChanges();
      });
    this.dashboardService.deviceGridTransparentBackdrop$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (backdropData: { isShow: boolean, zIndex?: number }) => {
        if (backdropData.isShow) {
          this.transparentBackdropZIndex = backdropData.zIndex;
        }
        this.isDeviceGridTransparentBackdrop = backdropData.isShow;
        this.cdRef.detectChanges();
      });
    // Timeline backdrop subscription
    this.dashboardService.timelineBackdrop$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (backdropData: { isShow: boolean, backgroundColor?: string, opacity?: number, zIndex?: number }) => {
        if (backdropData.isShow) {
          this.backdropColor = backdropData.backgroundColor;
          this.backdropOpacity = backdropData.opacity;
          this.backdropZIndex = backdropData.zIndex;
        }
        this.isTimelineBackdrop = backdropData.isShow;
        this.cdRef.detectChanges();
      });

    this.selfOrganizationUser$.pipe(
      takeUntil(this.destroyed$),
      withLatestFrom(this.selectedOrganization$, this.accessToken$)
    ).subscribe(([selfOrganizationUser, selectedOrganization, accessToken]: [SelfOrganizationUser, OrganizationWithToken, string]) => {
      if (selfOrganizationUser && !selfOrganizationUser.hasOnboarded) {
        this.dialog.open(WelcomeComponent)
          .afterClosed()
          .subscribe(() => {
            this.orgService.finishOnboarding(selectedOrganization.token).subscribe();
          });
      }

      if (selfOrganizationUser && accessToken) {
        const storedUser = {
          accessToken,
          userId: selfOrganizationUser.userId,
          email: selfOrganizationUser.email,
          firstName: selfOrganizationUser.firstName,
          lastName: selfOrganizationUser.lastName,
          orgId: selfOrganizationUser.orgId
        };
        localStorage.setItem('user', JSON.stringify(storedUser));
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  selectDeviceByNotification(notification: Airnote | TriggerEvent, organizationToken: string): void {
    const selectedDevice: Device = this.devices.find((device: Device) =>
      `${notification.deviceType}_${notification.deviceId}` === `${device.deviceType}_${device.deviceId}`
    );
    if (selectedDevice != null) {
      this.timelineComp.invokePinOnNotification(selectedDevice, <Airnote | TriggerEvent>notification, organizationToken);
    } else {
      alert(`There is no such device ${notification.deviceType} ${notification.deviceId}`);
    }
  }

  // Unpin graph and just leave device selection
  unpinGraphOnly(): void {
    this.timelineComp.unpinTooltip();
  }

  // Unpin graph and device selection
  deselectAll(): void {
    this.timelineComp.backToInitState();
    this.dashboardStore.dispatch(new EmptySelectedDevice());
  }

  pinnedNoti(noti: Airnote | TriggerEvent): void {
    this.dashboardService.pinGraph(noti);
  }

  removedNoti(): void {
    this.dashboardService.unpinGraph();
  }

  switchListPane(selectedPane: ListPaneType): void {
    this.dashboardStore.dispatch(new SwitchListPane(selectedPane));
  }
}
